// The data a Studio working page reads (the Hub overview and a Question page),
// and whether its docked panel is open. Shared so the pages don't import each other.

import { useEffect, useState } from "preact/hooks";
import type { HubId } from "../../../src/exchange/curator-api.ts";
import { activityByIntent, pickerName } from "../domain/activity.ts";
import { goldenByIntent, presetAsks } from "../domain/golden.ts";
import { pricingRows } from "../domain/pricing.ts";
import { hubSources } from "../domain/sample.ts";
import type { HubSummary } from "../domain/types.ts";
import { loadDemand, loadEvents, loadGolden } from "./actions.ts";
import type { HubData } from "./panels.tsx";
import { useStore, type StudioState } from "./store.ts";

/** The hub's golden questions by question, once /api/v1/golden has been read. */
export function useGolden(hub: HubSummary) {
  const golden = useStore((s) => s.golden);
  useEffect(() => { void loadGolden(); }, []);
  return { golden, byIntent: golden?.status === "ok" ? goldenByIntent(golden.value.questions, hub.hub.name) : undefined };
}

const PANEL_KEY = "studio-panel-open";
/** All hubs' docked panel: closed on a fresh visit, then as the curator last left it. */
export function usePanelOpen(defaultOpen = true): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(() => { try { const saved = localStorage.getItem(PANEL_KEY); return saved === null ? defaultOpen : saved !== "0"; } catch { return defaultOpen; } });
  return [open, (v) => { setOpen(v); try { localStorage.setItem(PANEL_KEY, v ? "1" : "0"); } catch { /* storage blocked: lasts this visit */ } }];
}

// The docked panel sits beside the work above 1100 pixels; at or below, it follows the work. See Design > UI, "Workspace".
const ROOM_FOR_PANEL = "(min-width: 1101px)";
// The hub the panel choice belongs to, and the choice. Opening a hub starts its panel open when there's room;
// moving between that hub's pages keeps the curator's choice; All hubs forgets it, so the next hub opens fresh.
let panelHub: HubId | null = null;
let panelOpen = false;
/** A hub page's docked panel: open whenever a hub is opened and the panel fits beside the work. */
export function useHubPanelOpen(hub: HubId): [boolean, (open: boolean) => void] {
  if (panelHub !== hub) { panelHub = hub; panelOpen = matchMedia(ROOM_FOR_PANEL).matches; }
  const [open, setOpen] = useState(panelOpen);
  // The same page can switch hubs (the picker), which counts as opening the new one.
  useEffect(() => setOpen(panelOpen), [hub]);
  return [open, (v) => { panelOpen = v; setOpen(v); }];
}
/** All hubs calls this, so the next hub opened starts with its panel open again. */
export const forgetHubPanel = () => { panelHub = null; };

/**
 * A hub's name as Studio shows it: its short name, or its full name where
 * another of the caller's hubs has the same short name. Studio finds hubs by
 * Hub ID, never by this.
 */
export const hubLabel = (name: string, hubs: StudioState["hubs"]) =>
  pickerName(name, hubs?.status === "ok" ? [...hubs.value.hubs.map((h) => h.hub.name), ...hubs.value.hubsWithoutToolset.map((r) => r.name)] : []);
export const useHubLabel = (name: string) => hubLabel(name, useStore((s) => s.hubs));

/** A hub's data from what the store has read: its events and 30-day demand, the golden questions, and the sample. */
export function hubDataFrom(hub: HubSummary, s: Pick<StudioState, "hubs" | "events" | "demand" | "golden" | "sample">): HubData {
  const events = s.events[hub.hub.id];
  const demand = s.demand[`${hub.hub.id}:30`];
  // Also the sample's key: a hub shown by its full name has no sample values.
  const short = hubLabel(hub.hub.name, s.hubs);
  const list = events?.status === "ok" ? events.value.events : [];
  const presets = s.golden?.status === "ok" ? presetAsks(hub, s.golden.value.questions) : new Set<string>();
  return {
    hub, short, sample: s.sample, activity: activityByIntent(list), pricing: pricingRows(hub, list, presets),
    demand: demand?.status === "ok" ? demand.value : null,
    ...(s.golden?.status === "ok" ? { golden: goldenByIntent(s.golden.value.questions, hub.hub.name) } : {}),
    sources: hubSources(s.sample, short, hub.manifest.reads),
  };
}

/** Everything the Hub overview and a Question page read, gathered once. */
export function useHubData(hub: HubSummary) {
  const events = useStore((s) => s.events[hub.hub.id]);
  const demand = useStore((s) => s.demand);
  const sample = useStore((s) => s.sample);
  const { golden } = useGolden(hub);
  useEffect(() => { void loadEvents(hub.hub.id); void loadDemand(hub.hub.id, 30); }, [hub.hub.id]);
  const all = useStore((s) => s.events);
  const hubs = useStore((s) => s.hubs);
  return { d: hubDataFrom(hub, { hubs, events: all, demand, golden, sample }), events };
}

